import { ReactNode } from "react";
import CustomersCount from "./components/CustomersCount";
import LeadsCount from "./components/LeadsCount";
import ContactCounts from "./components/ContactCounts";
import OrdersCount from "./components/OrdersCount";
import GroupTripsCount from "./components/GroupTripsCount";
import TourCount from "./components/TourCount";
import RevenueTotalAmount from "./components/RevenueTotalAmount";
import RefundTotalAmount from "./components/RefundTotalAmount";
import RevenueMonthly from "./components/RevenueMonthly";
import PaymentMethodStats from "./components/PaymentMethods";
import NewCustomersByMonth from "./components/NewCustomersByMonth";
import CustomerGenders from "./components/CustomerGenders";
import CustomerCityzenship from "./components/CustomerCityzenship";
import TicketsTopicsPie from "../Tickets/components/TicketsTopicsPie";
import ComplaintsChart from "../Tickets/components/ComplaintsChart";
import AntiRatingChart from "../Tickets/components/AntiRatingChart";
import TicketsResponseTimeTrend from "../Tickets/components/TicketsResponseTimeTrend";
import TicketsCloseReasonsBreakdown from "../Tickets/components/TicketsCloseReasonsBreakdown";

/**
 * Секции дашборда (в порядке отображения):
 * - kpi     — карточки-счётчики;
 * - charts  — графики по клиентам и выручке;
 * - tickets — аналитика по обращениям.
 */
export type DashboardSection = "kpi" | "charts" | "tickets";

export interface DashboardWidget {
  id: string;
  section: DashboardSection;
  /** Модули организации (org.modules), которые должны быть включены все. */
  requires: string[];
  /** Виджет зависит от выбранного периода (month). */
  usesPeriod?: boolean;
  /**
   * Ширина в секциях charts/tickets (сетка из 6 колонок):
   * full — вся строка, half — половина (по умолчанию), third — треть.
   * В секции kpi карточки раскладываются автоматически.
   */
  size?: "full" | "half" | "third";
  render: (ctx: { month: number }) => ReactNode;
}

export const SECTION_TITLES: Record<DashboardSection, string> = {
  kpi: "Показатели",
  charts: "Клиенты и выручка",
  tickets: "Обращения",
};

/** Пояснение рядом с заголовком секции. */
export const SECTION_HINTS: Partial<Record<DashboardSection, string>> = {
  tickets: "Период и филиал выбираются в каждом графике отдельно",
};

/**
 * Реестр виджетов. Порядок в массиве = порядок на странице.
 * Чтобы добавить виджет — допишите его сюда и укажите нужные модули.
 */
export const DASHBOARD_WIDGETS: DashboardWidget[] = [
  // --- KPI ---
  { id: "customers-count", section: "kpi", requires: ["customers"], usesPeriod: true, render: ({ month }) => <CustomersCount month={month} /> },
  { id: "leads-count", section: "kpi", requires: ["leads"], usesPeriod: true, render: ({ month }) => <LeadsCount month={month} /> },
  { id: "contacts-count", section: "kpi", requires: ["contacts"], usesPeriod: true, render: ({ month }) => <ContactCounts month={month} /> },
  { id: "orders-count", section: "kpi", requires: ["sub-orders"], usesPeriod: true, render: ({ month }) => <OrdersCount month={month} /> },
  { id: "group-trips-count", section: "kpi", requires: ["group-trips"], usesPeriod: true, render: ({ month }) => <GroupTripsCount month={month} /> },
  { id: "tours-count", section: "kpi", requires: ["tour-packages"], usesPeriod: true, render: ({ month }) => <TourCount month={month} /> },
  { id: "revenue-total", section: "kpi", requires: ["sub-orders"], usesPeriod: true, render: ({ month }) => <RevenueTotalAmount month={month} /> },
  { id: "refund-total", section: "kpi", requires: ["sub-orders"], usesPeriod: true, render: ({ month }) => <RefundTotalAmount month={month} /> },

  // --- Клиенты и выручка ---
  { id: "revenue-monthly", section: "charts", requires: ["sub-orders"], usesPeriod: true, size: "half", render: ({ month }) => <RevenueMonthly month={month} /> },
  { id: "payment-methods", section: "charts", requires: ["sub-orders"], usesPeriod: true, size: "third", render: ({ month }) => <PaymentMethodStats month={month} /> },
  { id: "new-customers-by-month", section: "charts", requires: ["customers"], size: "third", render: () => <NewCustomersByMonth /> },
  { id: "customer-citizenship", section: "charts", requires: ["customers"], size: "third", render: () => <CustomerCityzenship /> },
  { id: "customer-genders", section: "charts", requires: ["customers"], size: "third", render: () => <CustomerGenders /> },

  // --- Обращения ---
  { id: "tickets-topics", section: "tickets", requires: ["tickets"], render: () => <TicketsTopicsPie /> },
  { id: "tickets-complaints", section: "tickets", requires: ["tickets"], render: () => <ComplaintsChart /> },
  { id: "anti-rating-branches", section: "tickets", requires: ["tickets", "branches"], render: () => <AntiRatingChart kind="branches" /> },
  { id: "anti-rating-trips", section: "tickets", requires: ["tickets", "trains"], render: () => <AntiRatingChart kind="trips" /> },
  { id: "tickets-response-time", section: "tickets", requires: ["tickets"], size: "full", render: () => <TicketsResponseTimeTrend defaultWindowDays={30} /> },
  { id: "tickets-close-reasons", section: "tickets", requires: ["tickets"], size: "full", render: () => <TicketsCloseReasonsBreakdown defaultWindowDays={30} /> },
];

/** Виджеты, доступные организации с данным набором модулей. */
export const getAvailableWidgets = (modules: string[] | null | undefined): DashboardWidget[] => {
  const enabled = new Set(modules || []);
  return DASHBOARD_WIDGETS.filter((w) => w.requires.every((m) => enabled.has(m)));
};
